import { createSelector } from 'reselect';
import { RetentionMetricDetail } from '@fullstory/proto/fs/services/dx-objects';
import { AppState } from '../../types';
import { AppCacheStatus } from '@fullstory/redux-util/utils';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { RetentionMetricsCacheData } from './types';
import { pickBy } from 'lodash';
import { createArrayShallowEqualSelector } from '@fullstory/redux-util/utils/selectors';

// Data selectors
const getRetentionMetricsDataMap = (state: AppState) => state.retentionMetrics.data;

type RetentionMetricsDataMap = ReturnType<typeof getRetentionMetricsDataMap>;

export const makeGetRetentionMetricsDataArray = (storageIds: string[]) =>
  createArrayShallowEqualSelector(
    createSelector(
      getRetentionMetricsDataMap,
      (map: RetentionMetricsDataMap): RetentionMetricsCacheData[] =>
        storageIds.map(s => getRetentionMetricsData(map, s)),
    ),
    dataArray => dataArray,
  );

export const makeGetRetentionMetricsData = (storageId: string) =>
  createSelector(
    getRetentionMetricsDataMap,
    (map: RetentionMetricsDataMap): RetentionMetricsCacheData =>
      getRetentionMetricsData(map, storageId),
  );

function getRetentionMetricsData(map: RetentionMetricsDataMap, storageId: string) {
  const {
    status = AppCacheStatus.Initial,
    ready = false,
    data = null,
    safeMessage = null,
  } = map[storageId] ?? {};

  return { status, ready, data, storageId, safeMessage };
}

export const getSavedRetentionMetricsById = (state: AppState) =>
  state.retentionMetrics.retentionMetrics;
export const getActiveSavedRetentionMetricsById = createSelector(
  getSavedRetentionMetricsById,
  retentionMetrics =>
    pickBy(
      retentionMetrics,
      retentionMetric => !retentionMetric.retentionMetric?.metadata?.archived,
    ),
);
export const makeGetSavedRetentionMetric = (retentionMetricId: string) =>
  createSelector(
    getSavedRetentionMetricsById,
    savedRetentionMetricsById => savedRetentionMetricsById?.[retentionMetricId],
  );

// Alphabetical list of retention metrics
export const savedRetentionMetricsList = createSelector(
  getSavedRetentionMetricsById,
  retentionMetrics => {
    const seen = new Set<string>();
    const list: RetentionMetricDetail[] = [];
    for (const key in retentionMetrics) {
      const retentionMetric = retentionMetrics[key];
      if (
        retentionMetric?.retentionMetric?.retentionMetric?.id &&
        !seen.has(retentionMetric.retentionMetric.retentionMetric.id)
      ) {
        list.push(retentionMetric);
        seen.add(retentionMetric.retentionMetric.retentionMetric.id);
      }
    }
    return list.sort(nameAndIdCompare);
  },
);

export const activeRetentionMetricsList = createSelector(
  savedRetentionMetricsList,
  retentionMetrics =>
    retentionMetrics.filter(
      retentionMetric => !retentionMetric.retentionMetric?.metadata?.archived,
    ),
);

export const getStarredRetentionMetrics = createSelector(
  savedRetentionMetricsList,
  retentionMetrics => retentionMetrics.filter(retentionMetric => retentionMetric.starred),
);

export const recentRetentionMetricsList = createSelector(
  getSavedRetentionMetricsById,
  retentionMetrics => {
    const list: RetentionMetricDetail[] = [];
    for (const key in retentionMetrics) {
      const retentionMetric = retentionMetrics[key];
      if (retentionMetric?.lastViewed) list.push(retentionMetric);
    }
    return list.sort((a, b) => Timestamp.toMillis(b.lastViewed) - Timestamp.toMillis(a.lastViewed));
  },
);

export const nameAndIdCompare = (a: RetentionMetricDetail, b: RetentionMetricDetail) =>
  a.retentionMetric?.retentionMetric?.name
    .toLowerCase()
    .localeCompare(b.retentionMetric?.retentionMetric?.name?.toLowerCase() ?? '') ||
  a.retentionMetric?.retentionMetric?.id.localeCompare(
    b.retentionMetric?.retentionMetric?.id ?? '',
  ) ||
  0;

export const getRetentionMetricsAccess = (state: AppState) => state.retentionMetrics.access;

export const getAllRetentionMetricsData = (state: AppState) =>
  state.retentionMetrics.allRetentionMetrics;

export const getUnnamedRetentionMetrics = (state: AppState) =>
  state.retentionMetrics.unnamedRetentionMetrics;
